import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

export enum ProposalStatus {
  OPEN = 'OPEN',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
  PENDING_CREDIT = 'PENDING_CREDIT',
  PENDING_ACCEPT = 'PENDING_ACCEPT',
}

export interface Proposal {
  id: string;
  seq: number;
  clientId: string;
  clientName: string;
  clientCpfCnpj: string;
  userId: string;
  userName: string;
  status: ProposalStatus;
  creditAnalysisId?: string | null;
  deliveryAddressId?: string | null;
  payment?: ProposalPayment | null;
  created_at: string;
}

export interface ProposalQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: ProposalStatus;
  clientId?: string;
  userId?: string;
  code?: number;
  seller?: string;
  start_date?: string;
  end_date?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  pages: number;
  limit: number;
}

export interface CreateProposalPayload {
  clientId: string;
  clientName?: string;
  clientCpfCnpj: string;
  status: ProposalStatus;
  contactIds?: string[];
}

export interface ProposalPayment {
  paymentType: 'CASH' | 'INSTALLMENTS';
  dueDay: number;
  installments: number;
  entradaInCents: number;
  freteInCents: number;
  freteInEntrada: boolean;
  hasDownPayment?: boolean;
  downPaymentPercentage?: number;
}

export interface UpdateProposalPayload {
  clientId?: string;
  clientName?: string;
  clientCpfCnpj?: string;
  status?: ProposalStatus;
  creditAnalysisId?: string | null;
  deliveryAddressId?: string | null;
  contactIds?: string[];
}

export interface ProposalPaymentPayload {
  paymentType: 'CASH' | 'INSTALLMENTS';
  dueDay: number;
  installments: number;
  freteInCents: number;
  freteInEntrada: boolean;
  hasDownPayment?: boolean;
  downPaymentPercentage?: number;
}

export const proposalApi = {
  findMany: async (params?: ProposalQueryParams): Promise<PaginatedResponse<Proposal>> => {
    const { data } = await api.get<ApiPaginatedResponse<Proposal>>('/commercial/proposals', {
      params,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<Proposal> => {
    const { data } = await api.get<ApiResponse<Proposal>>(`/commercial/proposals/${id}`);
    return data.data;
  },

  create: async (payload: CreateProposalPayload): Promise<{ success: boolean; id?: string }> => {
    const { data } = await api.post<ApiResponse<{ id?: string }>>('/commercial/proposals', payload);
    return { success: true, id: data.data.id };
  },

  update: async (id: string, payload: UpdateProposalPayload): Promise<Proposal> => {
    const { data } = await api.patch<ApiResponse<Proposal>>(`/commercial/proposals/${id}`, payload);
    return data.data;
  },

  setPayment: async (id: string, payment: ProposalPaymentPayload): Promise<Proposal> => {
    const { data } = await api.patch<ApiResponse<Proposal>>(
      `/commercial/proposals/${id}/payment`,
      payment,
    );
    return data.data;
  },
};
